import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Youtube, Radio, Globe, ExternalLink, Loader2, Play, Film } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1589903308904-1010c2294adc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHxffGdvc3BlbCUyMHdvcnNoaXAlMjBtdXNpYyUyMGhlYWRwaG9uZXMlMjBwb2RjYXN0JTIwbWljcm9waG9uZXxlbnwwfHx8fDE3ODk5NTIzMTN8MA&ixlib=rb-4.1.0&q=85";

export default function Media() {
  const [media, setMedia] = useState(null);

  useEffect(() => {
    api.get("/media").then(({ data }) => setMedia(data)).catch(() => setMedia({ youtube: [], spotify: [], websites: [] }));
  }, []);

  return (
    <div data-testid="media-page" className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-10">
      <section className="grid lg:grid-cols-2 gap-10 items-center mb-12">
        <div className="animate-rise">
          <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-gold mb-4">
            <Radio className="w-4 h-4" /> Curated & Christ-exalting
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl font-700 text-ink leading-tight">Gospel Media Hub</h1>
          <p className="mt-4 text-ink/70 leading-relaxed max-w-lg">
            Sound teaching, faithful preaching, and worship for your daily walk — gathered from
            trusted voices that point to Christ. Watch and listen right here.
          </p>
        </div>
        <div className="hidden lg:block animate-fade-in">
          <img src={HERO} alt="Gospel media" className="rounded-2xl shadow-xl w-full aspect-[16/10] object-cover ring-1 ring-ink/10" />
        </div>
      </section>

      {!media ? (
        <div className="flex justify-center py-20 text-ink/40"><Loader2 className="w-8 h-8 animate-spin" /></div>
      ) : (
        <Tabs defaultValue="youtube">
          <TabsList data-testid="media-tab-filter" className="flex flex-wrap gap-2 bg-transparent p-0 h-auto justify-start mb-8">
            <TabsTrigger value="youtube" className="rounded-full border border-border bg-card px-5 py-2.5 data-[state=active]:bg-ink data-[state=active]:text-parchment font-600">
              <Youtube className="w-4 h-4 mr-2" /> Video Teaching
            </TabsTrigger>
            <TabsTrigger value="spotify" className="rounded-full border border-border bg-card px-5 py-2.5 data-[state=active]:bg-ink data-[state=active]:text-parchment font-600">
              <Radio className="w-4 h-4 mr-2" /> Podcasts & Audio
            </TabsTrigger>
            <TabsTrigger value="movies" className="rounded-full border border-border bg-card px-5 py-2.5 data-[state=active]:bg-ink data-[state=active]:text-parchment font-600">
              <Film className="w-4 h-4 mr-2" /> Films about Jesus
            </TabsTrigger>
            <TabsTrigger value="websites" className="rounded-full border border-border bg-card px-5 py-2.5 data-[state=active]:bg-ink data-[state=active]:text-parchment font-600">
              <Globe className="w-4 h-4 mr-2" /> Study Websites
            </TabsTrigger>
          </TabsList>

          <TabsContent value="youtube">
            <div className="grid md:grid-cols-2 gap-6">
              {media.youtube.map((v) => (
                <div key={v.channel_url} data-testid="media-youtube-card" className="bg-card rounded-2xl border border-border overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="aspect-video bg-ink-deep">
                    <iframe
                      className="w-full h-full"
                      src={v.embed_url}
                      title={v.channel}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <div className="p-5">
                    <span className="font-mono text-[11px] uppercase tracking-widest text-gold">Latest Uploads</span>
                    <h3 className="font-serif text-2xl font-600 text-ink mt-1 leading-snug">{v.channel}</h3>
                    <p className="text-sm text-ink/70 mt-2 leading-relaxed">{v.description}</p>
                    <a
                      href={v.channel_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="media-youtube-channel-link"
                      className="inline-flex items-center gap-1.5 mt-3 text-sm font-600 text-ink hover:text-gold transition-colors"
                    >
                      Visit channel <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="spotify">
            <div className="grid md:grid-cols-2 gap-6">
              {media.spotify.map((s) => (
                <div key={s.title} className="bg-card rounded-2xl border border-border p-5 hover:shadow-lg transition-shadow">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-olive">
                      <Play className="w-3 h-3" /> {s.kind}
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl font-600 text-ink leading-snug">{s.title}</h3>
                  <p className="text-sm text-ink/70 mt-1 mb-4 leading-relaxed">{s.description}</p>
                  {s.spotify_id ? (
                    <iframe
                      data-testid="media-spotify-player"
                      className="w-full rounded-xl"
                      style={{ borderRadius: "12px" }}
                      src={`https://open.spotify.com/embed/${s.embed_type}/${s.spotify_id}?utm_source=generator`}
                      width="100%"
                      height="232"
                      frameBorder="0"
                      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                      loading="lazy"
                      title={s.title}
                    />
                  ) : null}
                  <div className="flex flex-wrap gap-2 mt-4">
                    {!s.spotify_id && s.link_url && (
                      <a
                        href={s.link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="media-spotify-link"
                        className="inline-flex items-center gap-2 bg-olive hover:bg-ink text-parchment font-700 px-5 py-2.5 rounded-full transition-colors duration-300"
                      >
                        Find on Spotify <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                    {s.apple_url && (
                      <a
                        href={s.apple_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="media-apple-link"
                        className="inline-flex items-center gap-2 bg-ink hover:bg-gold hover:text-ink text-parchment font-700 px-5 py-2.5 rounded-full transition-colors duration-300"
                      >
                        Find on Apple <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="websites">
            <div className="grid sm:grid-cols-2 gap-5">
              {media.websites.map((w) => (
                <a
                  key={w.url}
                  href={w.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="media-website-link"
                  className="group bg-card rounded-2xl border border-border p-6 flex items-start gap-4 hover:border-gold hover:shadow-lg transition-all duration-300"
                >
                  <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-gold-light text-gold group-hover:bg-ink group-hover:text-gold transition-colors shrink-0">
                    <Globe className="w-6 h-6" />
                  </span>
                  <div>
                    <h3 className="font-serif text-xl font-600 text-ink flex items-center gap-2">
                      {w.name}
                      <ExternalLink className="w-4 h-4 text-ink/30 group-hover:text-gold transition-colors" />
                    </h3>
                    <p className="text-sm text-ink/70 mt-1 leading-relaxed">{w.desc}</p>
                  </div>
                </a>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="movies">
            <div className="grid md:grid-cols-2 gap-6">
              {media.movies?.map((m, i) => (
                <div key={i} data-testid="media-movie-card" className="bg-card rounded-2xl border border-border overflow-hidden hover:shadow-lg transition-shadow flex flex-col">
                  {m.video_id ? (
                    <div className="aspect-video bg-ink-deep">
                      <iframe
                        className="w-full h-full"
                        src={`https://www.youtube.com/embed/${m.video_id}`}
                        title={m.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : (
                    <div className="aspect-video relative overflow-hidden">
                      <img src={m.image} alt={m.title} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-ink-deep/90 via-ink-deep/20 to-transparent" />
                      <span className="absolute bottom-3 left-4 font-serif text-2xl font-600 text-parchment drop-shadow">{m.title}</span>
                    </div>
                  )}
                  <div className="p-5 flex flex-col flex-1">
                    <span className="font-mono text-[11px] uppercase tracking-widest text-gold">{m.kind} · {m.year}</span>
                    <h3 className="font-serif text-2xl font-600 text-ink mt-1 leading-snug">{m.title}</h3>
                    <p className="text-sm text-ink/70 mt-2 leading-relaxed flex-1">{m.description}</p>
                    {m.watch_url && (
                      <a
                        href={m.watch_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="media-movie-watch"
                        className="inline-flex items-center gap-2 mt-4 self-start bg-ink hover:bg-gold hover:text-ink text-parchment font-700 px-5 py-2.5 rounded-full transition-colors duration-300"
                      >
                        <Play className="w-4 h-4" /> Watch <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
